import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  DEFAULT_TTL,
  ERROR_TTL,
  toastStore,
  type ToastItem,
} from "./toast-store";

type ToastTimerRecord = {
  timerId?: number;
  expiry?: number;
  remaining?: number;
  hovering?: boolean;
};

const getToastClassName = (toast: ToastItem) => {
  if (toast.variant === "success") {
    return "border-state-success/30 bg-state-successSoft text-content-primary";
  }
  if (toast.variant === "error") {
    return "border-state-danger/30 bg-state-dangerSoft text-content-primary";
  }
  if (toast.variant === "warning") {
    return "border-state-warning/30 bg-state-warningSoft text-content-primary";
  }
  return "border-border-subtle bg-surface-primary text-content-primary";
};

export const ToastContainer = () => {
  const items = toastStore((state) => state.items);
  const dismissToast = toastStore((state) => state.dismissToast);
  const { t } = useTranslation("common");
  const timersRef = useRef<Record<string, ToastTimerRecord>>({});

  useEffect(() => {
    const currentIds = new Set(items.map((toast) => toast.id));

    Object.keys(timersRef.current).forEach((id) => {
      if (!currentIds.has(id)) {
        const timer = timersRef.current[id];
        if (timer?.timerId) {
          clearTimeout(timer.timerId);
        }
        delete timersRef.current[id];
      }
    });

    items.forEach((toast) => {
      const timer = timersRef.current[toast.id];
      if (timer && (timer.timerId || timer.hovering)) {
        return;
      }

      const ttl = toast.variant === "error" ? ERROR_TTL : DEFAULT_TTL;
      const timerId = window.setTimeout(() => {
        dismissToast(toast.id);
        delete timersRef.current[toast.id];
      }, ttl);

      timersRef.current[toast.id] = {
        timerId,
        expiry: Date.now() + ttl,
      };
    });

    return () => {
      Object.values(timersRef.current).forEach((timer) => {
        if (timer?.timerId) {
          clearTimeout(timer.timerId);
        }
      });
      timersRef.current = {};
    };
  }, [items, dismissToast]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex justify-center px-4"
    >
      <div className="flex w-full max-w-sm flex-col gap-3">
        {items.map((toast) => (
          <div
            key={toast.id}
            onMouseEnter={() => {
              const timer = timersRef.current[toast.id];
              if (!timer?.timerId) {
                return;
              }

              clearTimeout(timer.timerId);
              timer.remaining = timer.expiry
                ? Math.max(0, timer.expiry - Date.now())
                : undefined;
              delete timer.timerId;
              timer.hovering = true;
            }}
            onMouseLeave={() => {
              const timer = timersRef.current[toast.id];
              if (!timer?.hovering) {
                return;
              }

              const remaining =
                timer.remaining ??
                (toast.variant === "error" ? ERROR_TTL : DEFAULT_TTL);
              timer.timerId = window.setTimeout(() => {
                dismissToast(toast.id);
                delete timersRef.current[toast.id];
              }, remaining);
              timer.expiry = Date.now() + remaining;
              timer.hovering = false;
              delete timer.remaining;
            }}
            className={`pointer-events-auto rounded-xl border px-4 py-3 text-sm shadow-sm transition-all duration-200 ${getToastClassName(
              toast,
            )}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="break-words font-medium">
                  {toast.count > 1
                    ? t("toast.aggregate.multiple", {
                        count: toast.count,
                        title: toast.title,
                      })
                    : toast.title}
                </p>
                {toast.description && (
                  <p className="mt-1 break-words text-xs text-content-secondary">
                    {toast.description}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                <button
                  aria-label={t("common.aria.closeNotification")}
                  onClick={() => {
                    const timer = timersRef.current[toast.id];
                    if (timer?.timerId) {
                      clearTimeout(timer.timerId);
                    }
                    delete timersRef.current[toast.id];
                    dismissToast(toast.id);
                  }}
                  className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded-xl text-content-muted transition-all duration-200 hover:bg-surface-secondary hover:text-content-primary"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
